import Typography from 'components/Typography/Typography';
import { CardContainer, CardContent, SelectionContainer } from './CartWizard.elements';
import { Card } from 'pages/Documentation/documentation.elements';
import Checkbox from 'components/Checkbox';
import QuoteBanner from 'icons/indexed-icons/QuoteBanner';
import OrderBanner from 'icons/indexed-icons/OrderBanner';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { CartPriceType } from './helper';
import styledTheme from 'common/theme';
import { CartState } from 'types/parts/cart';
import { useTranslation } from 'react-i18next';

type Props = {
  handlePriceTypeSelection: (priceType: CartPriceType) => void;
  cart: CartState;
  selectedPriceType?: CartPriceType;
};

const CartSelectionStep = ({
  handlePriceTypeSelection,
  cart,
  selectedPriceType
}: Props): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  const [priceType, setPriceType] = useState({
    // set the default state for checkbox
    'none-price': ['none-price', 'mix', 'nill'].includes(selectedPriceType || ''),
    price: ['price', 'mix', 'nill'].includes(selectedPriceType || '')
  });

  useEffect(() => {
    // set the checkbox state based on selectedPriceType when component mounts or selectedPriceType changes
    // this ensure that in the case of both price types being selected, when no selection is made in step one
    // this useEffect will set the selection type to mix, otherwise, selection type will be nill even though both boxes are selected
    const hasBoth = priceType['none-price'] && priceType['price'];
    const hasPrice = priceType['price'];
    const hasNonePrice = priceType['none-price'];
    const selectedPrice = hasBoth
      ? 'mix'
      : hasPrice
      ? 'price'
      : hasNonePrice
      ? 'none-price'
      : 'nill';
    handlePriceTypeSelection(selectedPrice as CartPriceType);
  }, [priceType, handlePriceTypeSelection]);
  const handlePriceType = (e: ChangeEvent<HTMLInputElement>, name: CartPriceType) => {
    const { checked } = e.target;
    const priceSelection = {
      ...priceType,
      [name]: checked
    };
    const hasBoth = priceSelection['none-price'] && priceSelection['price'];
    const hasPrice = priceSelection['price'];
    const hasNonePrice = priceSelection['none-price'];
    const selectedPrice = hasBoth
      ? 'mix'
      : hasPrice
      ? 'price'
      : hasNonePrice
      ? 'none-price'
      : 'nill';

    setPriceType(priceSelection);
    handlePriceTypeSelection(selectedPrice);
  };

  const prosealItemCount = useMemo(() => {
    return cart?.items?.reduce(
      (acc, curr) => {
        if (cart.items.some((product) => product.selected)) {
          if (curr.selected) {
            const product = curr.item;
            const keys = product.isPriceMissing ? 'nonePriceItem' : 'priceItem';
            return { ...acc, [keys]: acc[keys] + 1 };
          } else return acc;
        } else {
          const product = curr.item;
          const keys = product.isPriceMissing ? 'nonePriceItem' : 'priceItem';
          return { ...acc, [keys]: acc[keys] + 1 };
        }
      },

      // setup accumulator initial value
      {
        nonePriceItem: 0,
        priceItem: 0
      }
    );
  }, []);
  return (
    <SelectionContainer>
      <Typography color={styledTheme.colors.gray800} className="heading" variant="h1">
        {t('make_selection_heading')}
      </Typography>
      <CardContainer>
        <Card height="auto" width="27rem">
          <CardContent
            htmlFor="checkbox-none-price"
            className={`${!prosealItemCount['nonePriceItem'] ? 'disabled' : undefined}`}
          >
            <Checkbox
              id="none-price"
              checked={priceType['none-price']}
              onChange={(e) => handlePriceType(e, 'none-price')}
              className="checkbox"
              margin="0"
              width={18}
              height={18}
              disabled={!prosealItemCount['nonePriceItem']}
            />
            <QuoteBanner />
            <Typography className="card-description" size={'1rem'} variant="h3">
              {t('quote_request_card_text')}
            </Typography>
            <Typography color={styledTheme.colors.gray700} weight="semi-bold">
              ({prosealItemCount['nonePriceItem']} Items)
            </Typography>
          </CardContent>
        </Card>
        <Card height="auto" width="27rem">
          <CardContent
            htmlFor="checkbox-price"
            className={`${!prosealItemCount['priceItem'] ? 'disabled' : undefined}`}
          >
            <Checkbox
              id="price"
              checked={priceType['price']}
              onChange={(e) => handlePriceType(e, 'price')}
              className="checkbox"
              margin="0"
              width={18}
              height={18}
              disabled={!prosealItemCount['priceItem']}
            />
            <OrderBanner />
            <Typography className="card-description" size={'1rem'} variant="h3">
              {t('checkout_with_price')}
            </Typography>
            <Typography color={styledTheme.colors.gray700} weight="semi-bold">
              ({prosealItemCount['priceItem']} Items)
            </Typography>
          </CardContent>
        </Card>
      </CardContainer>
    </SelectionContainer>
  );
};

export default CartSelectionStep;
